import React, { useEffect, useState } from "react";
import { Loader2, Globe2, BookOpenText, Award } from "lucide-react";
import { api } from "../lib/api";
import { usePageDescription } from "../lib/usePageDescription";
import PageHero from "../components/PageHero";

const BadgeImage = ({ code, className = "h-12 w-12" }) => (
  <img
    src={`https://images.habbo.com/c_images/album1584/${code}.gif`}
    alt={code}
    className={`hb-pixel-img ${className}`}
    loading="lazy"
  />
);

const Badges = () => {
  const [badges, setBadges] = useState([]);
  const [latest, setLatest] = useState([]);
  const [overviews, setOverviews] = useState({}); // { code: { total, hotels[] } }
  const [gamedataMap, setGamedataMap] = useState({}); // { code: { found_count, hotels[] } }
  const [loading, setLoading] = useState(true);
  const [loadingCode, setLoadingCode] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState("Alle");
  // Toggles aus Settings: standardmaessig false
  const [showManual, setShowManual] = useState(false);
  const [showOverview, setShowOverview] = useState(false);
  const pd = usePageDescription("badges", {
    title: "Badge-Übersicht",
    intro: "Klicke ein Badge an, um zu sehen wie viele Habbos es in jedem Hotel besitzen. Live-Daten kommen direkt aus der öffentlichen Habbo API.",
  });

  useEffect(() => {
    (async () => {
      try {
        const [b, l, s] = await Promise.all([api.listBadges(), api.latestBadges(30), api.getSettings()]);
        setBadges(b);
        setLatest(l);
        setShowManual(!!s.badges_show_manual);
        setShowOverview(!!s.badges_show_overview);
      } finally { setLoading(false); }
    })();
  }, []);

  const categories = ["Alle", ...Array.from(new Set(badges.map((b) => b.category)))];
  const filtered = filter === "Alle" ? badges : badges.filter((b) => b.category === filter);

  const loadOverview = async (code) => {
    if (gamedataMap[code] && (!showOverview || overviews[code])) return;
    setLoadingCode(code);
    try {
      const tasks = [
        gamedataMap[code] ? Promise.resolve(gamedataMap[code]) : api.badgeGamedataAll(code),
      ];
      if (showOverview) {
        tasks.push(overviews[code] ? Promise.resolve(overviews[code]) : api.getBadgeOverview(code));
      }
      const results = await Promise.all(tasks);
      setGamedataMap((prev) => ({ ...prev, [code]: results[0] }));
      if (showOverview) {
        setOverviews((prev) => ({ ...prev, [code]: results[1] }));
      }
    } finally {
      setLoadingCode(null);
    }
  };

  const onToggle = (code) => {
    if (expanded === code) {
      setExpanded(null);
    } else {
      setExpanded(code);
      loadOverview(code);
    }
  };

  return (
    <section className="mx-auto max-w-7xl px-4 py-10">
      <PageHero eyebrow={pd.eyebrow || "Badges"} icon={Award} title={pd.title} keywords={pd.keywords} intro={pd.intro} />

      {/* Neueste Badges */}
      {latest.length > 0 && (
        <div className="mt-8 rounded-2xl border border-white/10 bg-[#0f141b] p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#ffb84d]">Frisch hochgeladen</div>
              <h2 className="mt-1 text-lg font-bold text-white">Die {latest.length} neuesten Habbo-Badges</h2>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-10">
            {latest.map((b) => (
              <div key={b.code} className="card-hover flex flex-col items-center rounded-xl border border-white/10 bg-[#10141b] p-3 text-center" title={b.label}>
                <img src={b.image} alt={b.code} className="hb-pixel-img h-12 w-12" loading="lazy" />
                <div className="mt-2 text-[10px] font-mono text-[#ffb84d]">{b.code}</div>
                <div className="mt-0.5 line-clamp-2 text-xs text-zinc-200 leading-tight">{b.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-2">
        {showManual && categories.map((c) => (
          <button
            key={c}
            onClick={() => setFilter(c)}
            className={`rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${
              filter === c
                ? "border-[#7c4dff]/60 bg-[#7c4dff]/20 text-white"
                : "border-white/10 bg-white/5 text-zinc-300 hover:border-white/20"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : !showManual ? null : (
        <div className="mt-8 space-y-3" data-testid="manual-badges-list">
          {filtered.map((b) => {
            const open = expanded === b.code;
            const ov = overviews[b.code];
            return (
              <div key={b.id} className="rounded-xl border border-white/10 bg-[#0f141b]">
                <button onClick={() => onToggle(b.code)} className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left">
                  <div className="flex items-center gap-3">
                    <BadgeImage code={b.code} className="h-12 w-12" />
                    <div>
                      <div className="text-sm font-semibold text-white">{b.name}</div>
                      <div className="text-xs text-zinc-400">{b.code} · {b.category}{b.year ? ` · ${b.year}` : ""}</div>
                      {b.description && <div className="mt-0.5 text-xs text-zinc-500">{b.description}</div>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {showOverview && ov && (
                      <div className="text-right">
                        <div className="pixel-text text-base text-[#ffb84d]">{ov.total.toLocaleString("de-DE")}</div>
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500">Besitzer:innen</div>
                      </div>
                    )}
                    <Globe2 className="h-4 w-4 text-zinc-500" />
                  </div>
                </button>

                {open && (
                  <div className="border-t border-white/10 p-4 space-y-5">
                    {loadingCode === b.code ? (
                      <div className="flex items-center justify-center py-6 text-zinc-400">
                        <Loader2 className="h-5 w-5 animate-spin" />
                      </div>
                    ) : (
                      <>
                        {showOverview && ov && (
                          <div>
                            <div className="mb-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-[#9d7bff]">
                              <Globe2 className="h-3.5 w-3.5" /> Besitzer:innen pro Hotel
                            </div>
                            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 lg:grid-cols-5">
                              {ov.hotels.map((h) => (
                                <div key={h.hotel} className="rounded-lg border border-white/10 bg-[#10141b] p-3">
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="text-base">{h.flag}</span>
                                    <span className="text-xs text-zinc-400">{h.label}</span>
                                  </div>
                                  <div className="mt-2 flex items-baseline gap-1">
                                    <span className="pixel-text text-xl text-white">
                                      {h.count.toLocaleString("de-DE")}
                                    </span>
                                    <span className="text-xs text-zinc-500">Habbos</span>
                                  </div>
                                  {h.error && <div className="mt-1 text-[10px] text-rose-300">{h.error}</div>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Gamedata pro Hotel */}
                        {gamedataMap[b.code] && (
                          <div data-testid={`badge-${b.code}-gamedata-all`}>
                            <div className="mb-2 flex items-center justify-between">
                              <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-[#ffb84d]">
                                <BookOpenText className="h-3.5 w-3.5" /> Gamedata-Texte pro Land
                              </div>
                              <div className="text-[10px] text-zinc-500">
                                {gamedataMap[b.code].found_count} / {gamedataMap[b.code].hotels.length} Hotels
                              </div>
                            </div>
                            <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
                              {gamedataMap[b.code].hotels.map((g) => (
                                <div
                                  key={g.hotel}
                                  data-testid={`badge-${b.code}-gamedata-${g.hotel}`}
                                  className={`rounded-lg border p-3 ${g.found ? "border-amber-500/20 bg-amber-500/[0.04]" : "border-white/5 bg-[#10141b] opacity-60"}`}
                                >
                                  <div className="flex items-center gap-2 text-xs">
                                    <span className="text-base">{g.flag}</span>
                                    <span className="text-zinc-400">{g.label}</span>
                                  </div>
                                  {g.found ? (
                                    <>
                                      <div className="mt-1.5 text-sm font-bold text-white">{g.name || <span className="italic text-zinc-500">– kein Name –</span>}</div>
                                      {g.description && <div className="mt-1 text-xs text-zinc-300 whitespace-pre-line break-words">{g.description}</div>}
                                    </>
                                  ) : (
                                    <div className="mt-1.5 text-xs italic text-zinc-600">– nicht in Gamedata vorhanden –</div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="py-10 text-center text-sm text-zinc-500">Keine Badges gefunden.</div>
          )}
        </div>
      )}
    </section>
  );
};

export default Badges;
